import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import * as styles from './Login.module.scss';
import { Alert, Button, Divider, Form, Input, Typography, message } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { AppStore, userActions, UserStatus } from '@credistream/shared';
import { useAppDispatch, db } from '~/initializeStore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { FIREBASE_REGION } from '~/services/firebase';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const verifyTurnstileToken = db
  ? httpsCallable<{ token: string }, { body: { success: boolean } }>(
      getFunctions(db.app, FIREBASE_REGION),
      'users-verifyTurnstile'
    )
  : null;

interface OwnProps {}

type Props = OwnProps;

type LoginFormModel = {
  email: string;
  password: string;
};

const Login: FunctionComponent<Props> = () => {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [formController] = Form.useForm<LoginFormModel>();
  const { status: userStatus, error } = useSelector((store: AppStore) => store.user);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  useEffect(() => {
    if (userStatus === UserStatus.HAS_ERROR) {
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  }, [userStatus]);

  const onFinish = async (values: LoginFormModel) => {
    if (TURNSTILE_SITE_KEY && verifyTurnstileToken) {
      if (!turnstileToken) {
        message.error(t<string>('common:validationErrors.turnstileRequired'));

        return;
      }

      try {
        await verifyTurnstileToken({ token: turnstileToken });
      } catch {
        message.error(t<string>('common:messages.error.default'));
        turnstileRef.current?.reset();
        setTurnstileToken(null);

        return;
      }
    }

    dispatch(userActions.logInUser(values));
  };

  const generateForgotPasswordLink = () => {
    const email = formController.getFieldValue('email');
    if (email) {
      return `/auth/forgot-password/${email}`;
    }

    return '/auth/forgot-password';
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <Typography.Title level={4} className={styles.header}>
            {t<string>('loginPanel')}
          </Typography.Title>
          {userStatus === UserStatus.HAS_ERROR ? (
            <Alert
              className={styles.alert}
              type={userStatus === UserStatus.HAS_ERROR ? 'error' : 'info'}
              message={
                userStatus === UserStatus.HAS_ERROR ? (
                  t<string>(`common:firebaseAuthErrors.${error}`)
                ) : (
                  <Trans t={t} i18nKey="common:startJourney" />
                )
              }
            />
          ) : null}

          <Form<LoginFormModel>
            name="login-form"
            form={formController}
            layout="vertical"
            onFinish={onFinish}
            className={styles.form}
            size="large"
            validateTrigger={['onSubmit', 'onBlur']}
          >
            <Form.Item
              name="email"
              label={t<string>('login')}
              rules={[
                {
                  required: true,
                  message: t<string>('common:validationErrors.fieldIsRequired'),
                },
                {
                  type: 'email',
                  message: t<string>('common:validationErrors.wrongEmail'),
                },
              ]}
            >
              <Input autoFocus />
            </Form.Item>

            <Form.Item
              name="password"
              label={t<string>('password')}
              rules={[
                {
                  required: true,
                  message: t<string>('common:validationErrors.fieldIsRequired'),
                },
              ]}
            >
              <Input.Password />
            </Form.Item>

            {TURNSTILE_SITE_KEY ? (
              <Form.Item>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                  options={{ theme: 'light', language: i18n.language === 'pl' ? 'pl' : 'en' }}
                />
              </Form.Item>
            ) : null}

            <div className={styles.forgotPassword}>
              <div className={styles.forgotPasswordLink}>
                <Button onClick={() => history.push(generateForgotPasswordLink())} type="link">
                  {t<string>('common:button.forgotPassword')}
                </Button>
              </div>
              <Button
                type="primary"
                htmlType="submit"
                block
                className={styles.submitButton}
                loading={userStatus === UserStatus.LOGGING_IN}
              >
                {t<string>('common:button.login')}
              </Button>
            </div>
          </Form>

          <Divider>{t<string>('common:or')}</Divider>

          <div className={styles.register}>
            <Link to="/auth/register">{t<string>('common:button.register')}</Link>
            <h4 className={styles.registerClaim}>{t<string>('registerClaim')}</h4>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
